import type { SessionDetail } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronDown, FlaskConical, ListTree, Radar } from "lucide-react";
import { toast } from "sonner";
import { EnrichmentBadge, OutcomeChip, SentimentDelta } from "@/components/domain/analytics";
import { Hash } from "@/components/domain/code";
import { AgentLink, PageHeader, TimeAgo } from "@/components/domain/misc";
import { Page, QueryState } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown";
import { Field, Skeleton } from "@/components/ui/misc";
import { linkSearch, PATHS, sessionSearch } from "@/features/analytics/links";
import { AnnotatedTranscript } from "@/features/sessions/annotated-transcript";
import { SessionPanel } from "@/features/sessions/session-panel";
import { SimilarSessions } from "@/features/sessions/similar-sessions";
import { q } from "@/lib/api";
import { fmtCost, fmtDateTime, fmtDuration, fmtInt, fmtMoney } from "@/lib/format";

export const Route = createFileRoute("/_app/sessions/$sessionId")({
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(q.session(params.sessionId));
  },
  component: SessionPage,
});

const CHANNEL_LABEL: Record<SessionDetail["channel"], string> = { chat: "Chat", voice: "Voice", email: "Email", api: "API", slack: "Slack", ide: "IDE", batch: "Batch" };

function SessionPage() {
  const { sessionId } = Route.useParams();
  const session = useQuery(q.session(sessionId));
  return (
    <Page wide>
      <QueryState query={session} loading={<SessionSkeleton sessionId={sessionId} />}>
        {(s) => <SessionView session={s} />}
      </QueryState>
    </Page>
  );
}

function Breadcrumb({ sessionId }: { sessionId: string }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5">
      <Link to="/sessions" className="hover:text-fg hover:underline">
        Sessions
      </Link>
      <span aria-hidden>/</span>
      <span className="font-mono" aria-current="page">
        {sessionId}
      </span>
    </nav>
  );
}

function SessionSkeleton({ sessionId }: { sessionId: string }) {
  return (
    <>
      <PageHeader breadcrumb={<Breadcrumb sessionId={sessionId} />} title="Session" />
      <Skeleton className="h-24 w-full" />
      <div className="mt-3 grid grid-cols-[minmax(0,1fr)_380px] gap-3">
        <Skeleton className="h-[480px]" />
        <Skeleton className="h-[480px]" />
      </div>
    </>
  );
}

function SessionActions({ session: s }: { session: SessionDetail }) {
  return (
    <>
      <Button variant="outline" onClick={() => toast.success("Added to probe set", { description: `Session ${s.id} will run as a regression probe for ${s.agentName}. (Preview — not persisted yet.)` })}>
        <FlaskConical /> Add to probe set
      </Button>
      {s.traceIds.length === 1 ? (
        <Button variant="outline" asChild>
          <Link to="/traces/$traceId" params={{ traceId: s.traceIds[0]! }}>
            <ListTree /> Open trace
          </Link>
        </Button>
      ) : s.traceIds.length > 1 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <ListTree /> Open trace <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>{s.traceIds.length} traces</DropdownMenuLabel>
            {s.traceIds.map((t) => (
              <DropdownMenuItem key={t} asChild>
                <Link to="/traces/$traceId" params={{ traceId: t }} className="font-mono">
                  {t.slice(0, 16)}…
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <Button asChild>
        <Link to={PATHS.signalStudioNew} search={linkSearch({ fromSession: s.id })}>
          <Radar /> Create signal from this
        </Link>
      </Button>
    </>
  );
}

function SessionView({ session: s }: { session: SessionDetail }) {
  const funnels = useQuery(q.funnels());
  const counts = { user: 0, assistant: 0, tool: 0 };
  for (const t of s.transcript) if (t.role in counts) counts[t.role as keyof typeof counts]++;
  return (
    <>
      <PageHeader
        breadcrumb={<Breadcrumb sessionId={s.id} />}
        title={
          <span className="flex items-center gap-2">
            Session
            <Hash value={s.id} n={20} className="text-[13px] font-normal" />
          </span>
        }
        description={s.intent ? <span className="text-fg-2">“{s.intent}”</span> : "No intent: this session wasn't analyzed from content."}
        actions={<SessionActions session={s} />}
      />

      <Card className="mb-3 grid grid-cols-3 gap-x-6 gap-y-3 px-4 py-3 md:grid-cols-6">
        <Field label="Agent">
          <AgentLink id={s.agentId} name={s.agentName} />
        </Field>
        <Field label="Version">
          <span className="font-mono text-[12.5px]">{s.agentVersion}</span>
        </Field>
        <Field label="End user" hint={s.segment ? <Link to="/sessions" search={sessionSearch({ segment: s.segment })} className="hover:text-accent hover:underline">{s.segment}</Link> : undefined}>
          {s.endUserId ? (
            <Link to="/activity" search={{ affectedId: s.endUserId }} className="text-fg hover:text-accent hover:underline" title="Everything agents did and said affecting this end user">
              {s.endUser ?? s.endUserId}
            </Link>
          ) : (
            <span className="text-fg-3">Internal</span>
          )}
        </Field>
        <Field label="Channel">{CHANNEL_LABEL[s.channel]}</Field>
        <Field label="Started" hint={fmtDateTime(s.startedAt)}>
          <TimeAgo iso={s.startedAt} className="text-fg" />
        </Field>
        <Field label="Duration">
          <span className="tabular">{fmtDuration(s.durationMs)}</span>
        </Field>
        <Field label="Outcome">
          <OutcomeChip outcome={s.outcome} source={s.enrichment === "pending" ? undefined : s.outcomeSource} />
        </Field>
        <Field label="Turns" hint={`${counts.user} user · ${counts.assistant} agent · ${counts.tool} tool`}>
          <span className="tabular">{s.turns}</span>
        </Field>
        <Field label="Sentiment" hint={`${s.sentiment.start.toFixed(2)} → ${s.sentiment.end.toFixed(2)}`}>
          <SentimentDelta start={s.sentiment.start} end={s.sentiment.end} />
        </Field>
        <Field label="Cost" hint={`${fmtInt(s.tokens)} tokens`}>
          <span className="tabular">{fmtCost(s.costUsd)}</span>
        </Field>
        <Field label="Value">{s.value ? <span className="tabular">{fmtMoney(s.value, { cents: true })}</span> : <span className="text-fg-3">—</span>}</Field>
        <Field label="Enrichment">
          <EnrichmentBadge level={s.enrichment} />
        </Field>
      </Card>

      <div className="grid grid-cols-[minmax(0,1fr)_360px] items-start gap-3 2xl:grid-cols-[minmax(0,1fr)_420px]">
        <Card className="min-w-0">
          <CardHeader
            title="Transcript"
            description={s.transcript.length ? "PII-redacted. Stages on the left; sentiment, friction, signal hits and funnel steps in the margin." : undefined}
            actions={<span className="text-xs tabular text-fg-3">{s.transcript.length} turns</span>}
          />
          <AnnotatedTranscript session={s} funnels={funnels.data} />
        </Card>
        <div className="flex min-w-0 flex-col gap-3">
          <SessionPanel session={s} funnels={funnels.data} />
          <SimilarSessions sessionId={s.id} agentName={s.agentName} />
        </div>
      </div>
    </>
  );
}
